import React, { useMemo } from 'react';
import { 
    Box, 
    Typography, 
    Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import { setModal } from '../../../../Common/App';
import { StaffListModal } from '../Modals/StaffListModal';
import { getIcon } from '../../../../../Setup/Utils/iconHelper';

const StyledTreemapContainer = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, 200px)',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    minHeight: theme.spacing(50),
    justifyContent: 'center',
    '@media (max-width: 600px)': {
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: theme.spacing(1),
        padding: theme.spacing(0),
    },
}));

const TreeTile = styled(Box)(({ theme, covered }) => ({
    position: 'relative',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    background: theme.palette.info.main || theme.palette.grey[400],
    border: `1px solid ${theme.palette.info.main}dd`,
    borderRadius: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(1),
    height: theme.spacing(14),
    width: '200px',
    boxShadow: theme.shadows[2],
    '@media (max-width: 600px)': {
        width: '100%',
    },
    '&:hover': {
        opacity: 0.9,
        zIndex: 10,
        transform: 'scale(1.02)',
        boxShadow: theme.shadows[4],
    },
    '& .department-name': {
        color: theme.palette.common.white,
        fontWeight: theme.typography.fontWeightBold,
        fontSize: theme.typography.pxToRem(14),
        lineHeight: 1.2,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        textAlign: 'center',
        alignSelf: 'center',
    },
}));

export const Treemap = ({ departments = {}, weekRange = {} }) => {
    const dispatch = useDispatch();

    const departmentArray = useMemo(() => {
        return Object.values(departments)
            .sort((a, b) => b.staff_count - a.staff_count);
    }, [departments]);

    const handleTileClick = (department) => {
        dispatch(setModal({
            component: <StaffListModal department={department} weekRange={weekRange} />,
            open: true,
            title: `${department.department_name} Staff`
        }));
    };

    if (departmentArray.length === 0) {
        return (
            <Box sx={(theme) => ({ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: theme.spacing(50),
                flexDirection: 'column',
                gap: theme.spacing(2)
            })}>
                <Typography variant="h6" color="text.secondary">
                    No departments available
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Departments will appear here once data is loaded
                </Typography>
            </Box>
        );
    }

    return (
        <StyledTreemapContainer>
            {departmentArray.map((department) => {
                const IconComponent = getIcon(department.icon, 'outlined');
                const CheckCircleComponent = getIcon('check_circle', 'outlined');
                const isCovered = department.covered ? 'true' : 'false';
                
                return (
                    <TreeTile 
                        key={department.id}
                        covered={isCovered}
                        onClick={() => handleTileClick(department)}
                    >
                        <Tooltip title="Covered">
                            <CheckCircleComponent
                                sx={{
                                    display: isCovered === 'true' ? 'block' : 'none',
                                    fontSize: 22,
                                    color: 'success.light',
                                    mb: 1,
                                    position: 'absolute',
                                    top: 10,
                                    right: 10,
                                    opacity: 0.9
                                }}
                            />
                        </Tooltip>
                        <IconComponent 
                            sx={{ 
                                fontSize: 42, 
                                color: 'white',
                                mb: 1,
                                opacity: 0.9
                            }} 
                        />
                        <Typography className="department-name">
                            {department?.department_name} ({department?.scheduled_staff_count || 0})
                        </Typography>
                    </TreeTile>
                );
            })}
        </StyledTreemapContainer>
    );
};